/**
 * Modal para crear una nueva subcategoría.
 *
 * Este componente proporciona una interfaz de usuario para crear nuevas subcategorías
 * dentro de una categoría específica. Incluye validación en tiempo real, formateo
 * automático de texto y manejo de estados del formulario.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useEffect, useState } from "react";
import { Box, Divider, Modal, Card, Icon, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
export function SubCategoryCreate({
  open,
  handleClose,
  getFieldValue,
  handleFieldChange,
  handleSubmit,
  isFieldValid,
  categoryName = "",
}) {
  const [isDisabled, setIsDisabled] = useState(true);

  /**
   * Monitorea cambios en el input y actualiza el estado del botón
   */
  useEffect(() => {
    const fieldValue = getFieldValue("name") || "";
    setIsDisabled(!fieldValue.trim());
  }, [getFieldValue]);

  /**
   * Maneja el cambio en los inputs eliminando espacios al inicio
   * @param {string} fieldName - Nombre del campo
   * @param {string} value - Valor del campo
   */
  const handleInputChange = (fieldName, value) => {
    handleFieldChange(fieldName, value.trimStart());
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "400px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography id="modal-modal-title" component="h2">
            Crear subcategoría para: {categoryName}
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
            }}
            onClick={handleClose}>
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />
        <form onSubmit={handleSubmit}>
          <Box>
            <MDBox
              style={{
                display: "flex",
                justifyContent: "space-around",
                width: "100%",
                marginTop: "20px",
              }}>
              <MDBox style={{ width: "100%" }}>
                <MDInput
                  style={{ width: "100%" }}
                  label="Nombre de la subcategoría"
                  value={getFieldValue("name")}
                  error={!isFieldValid.name}
                  helperText={
                    getFieldValue("name") === "" && !isFieldValid.name
                      ? "El campo no puede estar vacío"
                      : !isFieldValid.name
                        ? "No se permiten números ni caracteres especiales"
                        : ""
                  }
                  FormHelperTextProps={{ className: !isFieldValid.name ? "Mui-error" : "" }}
                  onChange={(e) => {
                    const value = e.target.value;
                    const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
                    handleInputChange("name", formattedValue);
                  }}
                />
              </MDBox>
            </MDBox>
          </Box>
          <Divider variant="middle" style={{ marginTop: "20px" }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <MDButton
              color="error"
              sx={{
                flex: 1,
                mr: 1,
              }}
              onClick={handleClose}>
              Cancelar
            </MDButton>
            <MDButton
              type="submit"
              disabled={isDisabled}
              color="success"
              sx={{
                flex: 1,
                ml: 1,
              }}>
              Guardar
            </MDButton>
          </Box>
        </form>
      </Card>
    </Modal>
  );
}

SubCategoryCreate.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  getFieldValue: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isFieldValid: PropTypes.object.isRequired,
  categoryName: PropTypes.string,
};